import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import Navbar from '../components/layout/Navbar';
import StockTicker from '../components/layout/StockTicker';
import Footer from '../components/layout/Footer';
import { ArticleCardSidebar } from '../components/cards/ArticleCard';
import { Skeleton } from '../components/ui/skeleton';
import { Calendar, Eye, User, ChevronLeft, Share2, Facebook, Twitter, Linkedin, Link2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryColors = {
  'news': '#000',
  'personal-finance': '#000',
  'debt-management': '#dc2626',
  'small-business': '#000',
  'investing': '#000',
  'markets': '#dc2626',
};

const defaultImage = 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1200&q=80';

export default function ArticlePage() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [readingProgress, setReadingProgress] = useState(0);

  useEffect(() => {
    fetchArticle();
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    const handleScroll = () => {
      const winScroll = document.body.scrollTop || document.documentElement.scrollTop;
      const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrolled = (winScroll / height) * 100;
      setReadingProgress(scrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchArticle = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/articles/${id}`);
      setArticle(response.data);

      const relatedRes = await axios.get(`${API}/articles`, {
        params: { category: response.data.category, limit: 6 }
      });
      setRelatedArticles(relatedRes.data.filter((a) => a.id !== id).slice(0, 5));
    } catch (error) {
      console.error('Error fetching article:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard!');
  };

  const shareOnSocial = (platform) => {
    const url = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(article?.title || '');
    const links = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
      linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${title}`,
    };
    window.open(links[platform], '_blank', 'width=600,height=400');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <StockTicker />
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-12">
          <Skeleton className="w-full h-[400px] mb-8" />
          <Skeleton className="w-3/4 h-12 mb-4" />
          <Skeleton className="w-1/2 h-6 mb-8" />
          <Skeleton className="w-full h-[600px]" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-white">
        <StockTicker />
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-20 text-center">
          <h1 className="text-2xl text-black font-black" style={{ fontFamily: "'Merriweather', serif" }}>Article Not Found</h1>
          <Link to="/" className="text-red-600 hover:underline font-mono uppercase tracking-wider text-sm mt-4 inline-block">
            Return to Home
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="article-page">
      {/* Reading Progress Bar */}
      <div
        className="fixed top-0 left-0 h-1 reading-progress z-50 transition-all duration-150"
        style={{ width: `${readingProgress}%` }}
      />

      <StockTicker />
      <Navbar />

      {/* Article Header */}
      <header className="py-10 border-b-2 border-black">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <Link
            to={article.subcategory
              ? `/category/${article.category}/${article.subcategory}`
              : `/category/${article.category}`}
            className="inline-flex items-center text-xs font-mono text-zinc-500 hover:text-red-600 transition-colors mb-4 uppercase tracking-widest"
            data-testid="back-to-category"
          >
            <ChevronLeft className="w-4 h-4 mr-1" strokeWidth={2} />
            Back to {article.category?.replace('-', ' ')}
          </Link>

          <span
            className="inline-block text-[10px] font-mono uppercase tracking-widest px-2 py-1 mb-4 font-bold"
            style={{
              backgroundColor: categoryColors[article.category] || '#000',
              color: '#fff',
            }}
          >
            {article.category?.replace('-', ' ')}
          </span>

          <h1
            className="text-3xl md:text-5xl lg:text-6xl font-black text-black mb-6 leading-[0.95] tracking-tight"
            style={{ fontFamily: "'Merriweather', serif" }}
            data-testid="article-title"
          >
            {article.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-xs text-zinc-500 font-mono uppercase tracking-wider">
            {article.author && (
              <span className="flex items-center font-bold text-black">
                <User className="w-4 h-4 mr-1" strokeWidth={2} />
                {article.author}
              </span>
            )}
            <span className="flex items-center">
              <Calendar className="w-4 h-4 mr-1" strokeWidth={2} />
              {formatDate(article.published_at)}
            </span>
            <span className="flex items-center">
              <Eye className="w-4 h-4 mr-1" strokeWidth={2} />
              {article.view_count || 0} views
            </span>
          </div>
        </div>
      </header>

      {/* Article Content */}
      <article className="py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-8">
              {/* Featured Image */}
              {article.image_url && (
                <div className="aspect-video overflow-hidden border-2 border-black mb-8">
                  <img
                    src={article.image_url || defaultImage}
                    alt={article.title}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = defaultImage; }}
                  />
                </div>
              )}

              {/* Article Body */}
              <div
                className="prose prose-lg max-w-none"
                data-testid="article-content"
              >
                {article.excerpt && (
                  <p
                    className="text-xl text-zinc-600 leading-relaxed mb-8 gold-border-left pl-4"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.excerpt) }}
                  />
                )}
                {article.content ? (
                  <div
                    className="text-zinc-800 leading-relaxed space-y-4"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content) }}
                  />
                ) : (
                  <p className="text-zinc-400 font-mono text-sm uppercase tracking-wider">Full article content is not available.</p>
                )}
              </div>

              {/* Share Section */}
              <div className="mt-12 pt-8 border-t-2 border-black">
                <h4 className="text-[10px] font-mono font-bold text-zinc-500 uppercase tracking-widest mb-4">
                  Share this article
                </h4>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => shareOnSocial('facebook')}
                    className="p-3 bg-black text-white hover:bg-red-600 transition-colors"
                    data-testid="share-facebook"
                  >
                    <Facebook className="w-4 h-4" strokeWidth={2} />
                  </button>
                  <button
                    onClick={() => shareOnSocial('twitter')}
                    className="p-3 bg-black text-white hover:bg-red-600 transition-colors"
                    data-testid="share-twitter"
                  >
                    <Twitter className="w-4 h-4" strokeWidth={2} />
                  </button>
                  <button
                    onClick={() => shareOnSocial('linkedin')}
                    className="p-3 bg-black text-white hover:bg-red-600 transition-colors"
                    data-testid="share-linkedin"
                  >
                    <Linkedin className="w-4 h-4" strokeWidth={2} />
                  </button>
                  <button
                    onClick={copyLink}
                    className="p-3 border-2 border-black text-black hover:bg-black hover:text-white transition-colors"
                    data-testid="copy-link"
                  >
                    <Link2 className="w-4 h-4" strokeWidth={2} />
                  </button>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-4">
              <div className="sticky top-24 space-y-8">
                {/* Related Articles */}
                <div className="border-2 border-black p-6">
                  <h3
                    className="font-mono font-bold text-xs uppercase tracking-widest border-b-2 border-black pb-2 mb-4 text-black"
                  >
                    Related Articles
                  </h3>
                  <div className="space-y-4">
                    {relatedArticles.length > 0 ? (
                      relatedArticles.map((related) => (
                        <ArticleCardSidebar key={related.id} article={related} />
                      ))
                    ) : (
                      <p className="text-zinc-400 text-sm font-mono">No related articles found.</p>
                    )}
                  </div>
                </div>

                {/* Share Widget */}
                <div className="border-2 border-black p-6">
                  <h3 className="font-mono font-bold text-xs uppercase tracking-widest border-b-2 border-black pb-2 mb-4 flex items-center text-black">
                    <Share2 className="w-4 h-4 mr-2 text-red-600" strokeWidth={2} />
                    Share
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => shareOnSocial('facebook')}
                      className="flex items-center justify-center p-2 border-2 border-black text-black hover:bg-black hover:text-white text-xs font-mono font-bold uppercase tracking-wider transition-colors"
                    >
                      <Facebook className="w-3 h-3 mr-1" strokeWidth={2} />
                      FB
                    </button>
                    <button
                      onClick={() => shareOnSocial('twitter')}
                      className="flex items-center justify-center p-2 border-2 border-black text-black hover:bg-black hover:text-white text-xs font-mono font-bold uppercase tracking-wider transition-colors"
                    >
                      <Twitter className="w-3 h-3 mr-1" strokeWidth={2} />
                      TW
                    </button>
                    <button
                      onClick={() => shareOnSocial('linkedin')}
                      className="flex items-center justify-center p-2 border-2 border-black text-black hover:bg-black hover:text-white text-xs font-mono font-bold uppercase tracking-wider transition-colors"
                    >
                      <Linkedin className="w-3 h-3 mr-1" strokeWidth={2} />
                      LI
                    </button>
                    <button
                      onClick={copyLink}
                      className="flex items-center justify-center p-2 border-2 border-black text-black hover:bg-black hover:text-white text-xs font-mono font-bold uppercase tracking-wider transition-colors"
                    >
                      <Link2 className="w-3 h-3 mr-1" strokeWidth={2} />
                      Link
                    </button>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}
